import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

export const rejectShowcaseReplacementSave = async () => {
  throw new Error('A visible product page cannot replace website membership. Use an item action.');
};

export default function useShowcaseProductPage({ server, instanceUniqueId, headers, onLoaded }) {
  const [criteria, setCriteria] = useState({ page: 1, rpp: 48, q: '' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mutating, setMutating] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const generation = useRef(0);
  const mutationInFlight = useRef(false);
  const currentScope = useRef(instanceUniqueId);
  currentScope.current = instanceUniqueId;
  const endpoint = `${server}/promoshowcase/instances/${instanceUniqueId}/products`;

  const reload = useCallback(async () => {
    const requestId = ++generation.current;
    if (!instanceUniqueId || currentScope.current !== instanceUniqueId) return;
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(endpoint, { ...headers, params: { paginated: true, ...criteria } });
      if (requestId !== generation.current) return;
      if (!response.data?.pagination) throw new Error('The server did not return product pagination. Refresh after updating the server.');
      setResult({ instanceUniqueId, ...response.data });
      onLoaded(response.data);
      return response.data;
    } catch (requestError) {
      if (requestId === generation.current) setError(requestError.response?.data?.message || requestError.message || 'Unable to load products.');
    } finally {
      if (requestId === generation.current) setLoading(false);
    }
  }, [criteria, endpoint, headers, instanceUniqueId, onLoaded]);

  useEffect(() => {
    reload();
    return () => { generation.current += 1; };
  }, [reload]);

  const changeCriteria = useCallback((patch) => {
    if (editorOpen || mutationInFlight.current) return;
    // Invalidate synchronously, before the next effect, so a late previous page cannot win.
    generation.current += 1;
    setCriteria(current => ({ ...current, ...patch, ...('q' in patch || 'rpp' in patch ? { page: 1 } : {}) }));
  }, [editorOpen]);

  const mutate = useCallback(async (type, key, product, onAcknowledged) => {
    if (mutationInFlight.current) throw new Error('Wait for the current product action to finish.');
    mutationInFlight.current = true;
    generation.current += 1;
    setMutating(true);
    try {
      await axios.post(`${endpoint}/items`, { type, key, product }, headers);
      if (currentScope.current === instanceUniqueId) {
        onAcknowledged?.();
        await reload();
      }
    } catch (mutationError) {
      if (currentScope.current === instanceUniqueId) {
        setLoading(false);
        setError(mutationError.response?.data?.message || mutationError.message || 'Unable to update product.');
      }
      throw mutationError;
    } finally {
      mutationInFlight.current = false;
      setMutating(false);
    }
  }, [endpoint, headers, instanceUniqueId, reload]);

  const data = result?.instanceUniqueId === instanceUniqueId ? result : null;
  const readSyncSession = useCallback(async () => {
    const response = await axios.get(`${endpoint}/sync/status`, headers);
    if (response.data?.success !== true) throw new Error('Sync status is unavailable.');
    return response.data.session || null;
  }, [endpoint, headers]);
  return { data, criteria, loading, error, mutating, editorOpen, setEditorOpen, changeCriteria, reload, mutate, readSyncSession };
}

export const showcaseEditedProduct = (original, updated) => ({
  ...original, ...updated,
  ProductId: original.ProductId, Source: original.Source,
  id: original.id, cartLineId: original.cartLineId,
  showcaseIdentity: original.showcaseIdentity,
  Pricings: updated.pricings ?? updated.Pricings ?? original.Pricings ?? original.pricings ?? [],
  pricings: updated.pricings ?? updated.Pricings ?? original.pricings ?? original.Pricings ?? [],
  Decorations: updated.decorations ?? updated.Decorations ?? original.Decorations ?? original.decorations ?? [],
  decorations: updated.decorations ?? updated.Decorations ?? original.decorations ?? original.Decorations ?? [],
});
